import type { RevisionContent } from '../schema/v1'

/**
 * El compás y el pulso de un arreglo (§3.3, «pulso explícito»). Sin compás se supone 4/4 y se
 * dice que es una suposición: una estimación nunca se presenta como dato (§1.1).
 */
export type BeatUnit = NonNullable<RevisionContent['beatUnit']>

export const DEFAULT_TIME_SIGNATURE = '4/4'

export interface Meter {
  timeSignature: string
  beatUnit: BeatUnit
  /** Pulsos por compás: 4 en 4/4, 3 en 3/4, 2 en 6/8 contado a negra con puntillo. */
  beatsPerBar: number
  /** Verdadero si la ficha no trae compás y se ha supuesto 4/4. */
  assumed: boolean
}

const UNIT_VALUE: Record<BeatUnit, number> = {
  '1/2': 1 / 2,
  '1/4': 1 / 4,
  '3/8': 3 / 8,
  '1/8': 1 / 8,
}

/** La figura que se cuenta como pulso si la ficha no lo dice: en 6/8, 9/8 y 12/8, la negra con puntillo. */
export function defaultBeatUnit(timeSignature: string): BeatUnit {
  const [numerator, denominator] = parseTimeSignature(timeSignature)
  if (denominator === 8 && numerator > 3 && numerator % 3 === 0) return '3/8'
  if (denominator <= 2) return '1/2'
  if (denominator >= 8) return '1/8'
  return '1/4'
}

function parseTimeSignature(timeSignature: string): [number, number] {
  const match = /^(\d+)\/(\d+)$/.exec(timeSignature)
  if (!match) return [4, 4]
  return [Number(match[1]), Number(match[2])]
}

export function meterOf(content: Pick<RevisionContent, 'timeSignature' | 'beatUnit'>): Meter {
  const timeSignature = content.timeSignature ?? DEFAULT_TIME_SIGNATURE
  const beatUnit = content.beatUnit ?? defaultBeatUnit(timeSignature)
  const [numerator, denominator] = parseTimeSignature(timeSignature)
  const exact = numerator / denominator / UNIT_VALUE[beatUnit]
  // Un compás que no cabe en pulsos enteros (7/16 contado a corcheas) se cuenta por su numerador.
  const beatsPerBar = Number.isInteger(exact) && exact >= 1 ? exact : numerator
  return { timeSignature, beatUnit, beatsPerBar, assumed: content.timeSignature === undefined }
}

/** «Compás 3, pulso 2», contando desde 1, para un pulso de la canción contado desde 0. */
export function barAndBeat(beat: number, beatsPerBar: number): { bar: number; beat: number } {
  const whole = Math.floor(beat + 1e-9)
  return {
    bar: Math.floor(whole / beatsPerBar) + 1,
    beat: (((whole % beatsPerBar) + beatsPerBar) % beatsPerBar) + 1,
  }
}
